/** @jsxImportSource @emotion/react */
import { createContext, useContext, useState } from 'react'
import { useDispatch } from 'react-redux'

import {
  MapLayer,
  updateMapStateProperty,
  updateSelectedFeature,
} from '@app/redux/riskMap/riskMapSlice'
import { removeSelectedMarkerFromMapIfExists } from '@app/components/Molecules/MapView/GenericMapView/GenericMapView.utilities'
import { HazardOptionStateByHazard } from '@app/components/Molecules/RiskSideDrawerContent/tabs/InformativePanel/LayersTab/HazardControls'
import { infoLayerFocusAtom, RiskSideDrawerLayerTabs } from '@app/state/layerAtom'
import type { RiskWorkspace } from '@app/types'
import { useSetAtom } from 'jotai'
import { useGenericMapContext } from '../GenericMapContext'
import {
  GenericMapContextInterface,
  MapProviderProps,
} from '../GenericMapContext/GenericMapContext.types'
import { handleUpdateVisibleAssets } from '../utils/assets'
import {
  handleAddRegionMasksLayers,
  handleRemoveRegionMasksLayers,
  handleSetLayerVisibility,
  handleToggleLayerInteractivity,
  handleToggleLayerVisibility,
  loadRegionFeatures,
} from '../utils/layers'
import {
  AddRegionMasksLayersProps,
  ClearMapProps,
  RemoveLayerByIdProps,
  RemoveRegionMasksLayersProps,
  SetLayerVisibilityProps,
  ToggleLayerVisibilityProps,
  UpdateLayersProps,
  UpdateVisibleAssetsProps,
} from './RiskMapContext.types'
import { workspaceMatchesMapLayers } from '@app/components/Risk/Workspaces/utils/Workspaces.utils'
import { logDataDogAction } from '@app/utils/logger'
import { DATADOG_ACTIONS } from '@app/app-constants'

export interface RiskMapContext extends GenericMapContextInterface {
  cascadingAssetNDays: { [assetId: string]: number | null }
  setCascadingAssetNDays: (assetId: string, nDays: number | null) => void
  showAddLayersModalTab: string
  hazardOptionState: HazardOptionStateByHazard
  prevHazardOptionState: HazardOptionStateByHazard | null
  setHazardOptionState: React.Dispatch<React.SetStateAction<HazardOptionStateByHazard>>
  setPrevHazardOptionState: React.Dispatch<React.SetStateAction<HazardOptionStateByHazard | null>>
  addRegionMasksLayers: (props: AddRegionMasksLayersProps) => void
  removeRegionMasksLayers: (props: RemoveRegionMasksLayersProps) => void
  setLayerVisibility: (props: SetLayerVisibilityProps) => void
  toggleLayerVisibility: (props: ToggleLayerVisibilityProps) => void
  toggleLayerInteractivity: (props: ToggleLayerVisibilityProps) => void
  updateVisibleAssets: (props: UpdateVisibleAssetsProps) => void
  setShowAddLayersModalTab: (type: string) => void
  selectFeature: (
    feature: mapboxgl.MapboxGeoJSONFeature,
    layer: MapLayer,
    layerCategory: RiskSideDrawerLayerTabs,
  ) => boolean
  deselectFeature: () => void
  activeWorkspace: RiskWorkspace | null
  setActiveWorkspace: React.Dispatch<React.SetStateAction<RiskWorkspace | null>>
}

export const MapContext = createContext({} as RiskMapContext)

export function MapProvider({ children }: MapProviderProps) {
  const dispatch = useDispatch()
  const [hazardOptionState, setHazardOptionState] = useState({} as HazardOptionStateByHazard)
  const [prevHazardOptionState, setPrevHazardOptionState] =
    useState<HazardOptionStateByHazard | null>(null)
  const [showAddLayersModalTab, setShowAddLayersModalTab] = useState('')
  const genericMapContext = useGenericMapContext()
  const [cascadingAssetNDays, setCascadingAssetNDaysState] = useState<{
    [assetId: string]: number | null
  }>({})
  const [activeWorkspace, setActiveWorkspace] = useState(null as RiskWorkspace | null)
  const setSideDrawerInfoLayerFocus = useSetAtom(infoLayerFocusAtom)

  const setLayerVisibility = ({
    showLayers,
    hideLayers,
    allLayers,
    map,
  }: SetLayerVisibilityProps) => {
    if (!map) return
    handleSetLayerVisibility({
      showLayers,
      hideLayers,
      map,
      allLayers,
      dispatch,
    })
  }

  const updateVisibleAssets = ({ visibleAssets, map }: UpdateVisibleAssetsProps) => {
    if (!map) return

    handleUpdateVisibleAssets({ visibleAssets, dispatch })
  }

  const removeLayerById = ({ layerId, map }: RemoveLayerByIdProps) => {
    if (!map) return
    genericMapContext.removeLayerById({ layerId, map })
    if (hazardOptionState[layerId]) {
      setHazardOptionState((prev) => {
        const newState = { ...prev }
        delete newState[layerId]
        return newState
      })
      if (prevHazardOptionState && prevHazardOptionState[layerId]) {
        delete prevHazardOptionState[layerId]
      }
    }
  }
  const setCascadingAssetNDays = (assetId: string, nDays: number | null) => {
    setCascadingAssetNDaysState((prev) => ({
      ...prev,
      [assetId]: nDays,
    }))
  }

  const addRegionMasksLayers = ({ regionMasks, map }: AddRegionMasksLayersProps) => {
    if (!map) return

    loadRegionFeatures(regionMasks.map((region) => region.region)).then((regionFeatures) => {
      handleAddRegionMasksLayers({ map, regionFeatures })
      genericMapContext.setRegionMasks(regionMasks)
    })
  }

  const clearMap = (props: ClearMapProps) => {
    if (!genericMapContext.map) return
    genericMapContext.clearMap(props)
    if (Object.keys(hazardOptionState).length) {
      setHazardOptionState({})
      setPrevHazardOptionState(null)
    }
    deselectFeature()
  }

  const deselectFeature = () => {
    dispatch(
      updateSelectedFeature({
        selectedFeature: null,
        selectedLayer: null,
      }),
    )
    dispatch(updateMapStateProperty({ selectedMarker: null }))
    removeSelectedMarkerFromMapIfExists()
  }

  const selectFeature = (
    feature: mapboxgl.MapboxGeoJSONFeature,
    layer: MapLayer,
    layerCategory: RiskSideDrawerLayerTabs,
  ) => {
    if (!genericMapContext.map) return false
    setSideDrawerInfoLayerFocus(layerCategory)
    dispatch(
      updateSelectedFeature({
        selectedFeature: feature,
        selectedLayer: layer,
      }),
    )

    logDataDogAction(DATADOG_ACTIONS.SELECT_FEATURE, {
      id: layer.type ?? layer.id,
      type: layer.type ? 'asset' : 'information',
    })

    return true
  }

  const updateLayers = ({ layers }: UpdateLayersProps) => {
    genericMapContext.updateLayers({ layers })
    const layerIds = layers.map((layer) => `${layer.hazard_id}`).filter((id) => id)
    const idsToRemove = Object.keys(hazardOptionState).filter((id) => !layerIds.includes(id))
    if (idsToRemove.length) {
      setHazardOptionState((prev) => {
        const newState = { ...prev }
        idsToRemove.forEach((id) => {
          delete newState[id]
        })
        return newState
      })
      if (prevHazardOptionState) {
        idsToRemove.forEach((id) => {
          delete prevHazardOptionState[id]
        })
      }
    }

    // Validate active workspace whenever layers are updated
    if (activeWorkspace && !workspaceMatchesMapLayers(activeWorkspace, layers)) {
      setActiveWorkspace(null)
    }
  }

  const toggleLayerVisibility = ({ layer, layers }: ToggleLayerVisibilityProps) => {
    handleToggleLayerVisibility({ layer, layers, dispatch })
  }

  const toggleLayerInteractivity = ({ layer, layers }: ToggleLayerVisibilityProps) => {
    handleToggleLayerInteractivity({ layer, layers, dispatch })
  }

  const removeRegionMasksLayers = ({ map }: RemoveRegionMasksLayersProps) => {
    if (!map) return

    handleRemoveRegionMasksLayers({ map })
    genericMapContext.setRegionMasks(null)
  }

  return (
    <MapContext.Provider
      value={{
        ...genericMapContext,
        clearMap,
        removeLayerById,
        updateLayers,
        addRegionMasksLayers,
        showAddLayersModalTab,
        hazardOptionState,
        prevHazardOptionState,
        setHazardOptionState,
        setPrevHazardOptionState,
        removeRegionMasksLayers,
        setLayerVisibility,
        toggleLayerVisibility,
        toggleLayerInteractivity,
        updateVisibleAssets,
        setShowAddLayersModalTab,
        selectFeature,
        deselectFeature,
        cascadingAssetNDays,
        setCascadingAssetNDays,
        activeWorkspace,
        setActiveWorkspace,
      }}
    >
      {children}
    </MapContext.Provider>
  )
}

export const useMap = () => useContext(MapContext)
